import { Mic, MicOff, Loader2, Volume2, Radio } from "lucide-react";
import type { VoiceState } from "../hooks/useVoice.js";

interface VoiceButtonProps {
  state: VoiceState;
  volume: number;
  onClick: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const stateConfig: Record<VoiceState, { label: string; color: string; pulseColor: string }> = {
  idle: { label: "Falar", color: "bg-secondary text-muted-foreground hover:bg-secondary/80", pulseColor: "" },
  "wake-listening": { label: "Diga 'Olá Megan'", color: "bg-yellow-500/20 text-yellow-500 border-yellow-500/30", pulseColor: "bg-yellow-500/20" },
  listening: { label: "Escutando...", color: "bg-red-500 text-white", pulseColor: "bg-red-500/30" },
  processing: { label: "Processando...", color: "bg-primary text-primary-foreground", pulseColor: "" },
  speaking: { label: "Megan falando...", color: "bg-primary/80 text-primary-foreground", pulseColor: "bg-primary/20" },
};

export function VoiceButton({ state, volume, onClick, className = "", size = "md" }: VoiceButtonProps) {
  const config = stateConfig[state];
  const isActive = state === "listening" || state === "wake-listening" || state === "speaking";
  const sizeClasses = size === "sm" ? "w-8 h-8" : size === "lg" ? "w-12 h-12" : "w-10 h-10";
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : size === "lg" ? "w-5 h-5" : "w-4 h-4";

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      {/* Pulse ring for active states */}
      {isActive && config.pulseColor && (
        <>
          <span
            className={`absolute rounded-full ${config.pulseColor} animate-ping`}
            style={{
              width: `${(size === "sm" ? 32 : size === "lg" ? 48 : 40) + 16}px`,
              height: `${(size === "sm" ? 32 : size === "lg" ? 48 : 40) + 16}px`,
              animationDuration: "1.5s",
            }}
          />
          {/* Volume ring for listening */}
          {state === "listening" && (
            <span
              className="absolute rounded-full border-2 border-red-400/60 transition-transform duration-75"
              style={{
                width: `${(size === "sm" ? 32 : size === "lg" ? 48 : 40) + 8 + volume * 20}px`,
                height: `${(size === "sm" ? 32 : size === "lg" ? 48 : 40) + 8 + volume * 20}px`,
                opacity: 0.4 + volume * 0.6,
              }}
            />
          )}
        </>
      )}

      {/* Button */}
      <button
        onClick={onClick}
        disabled={state === "processing"}
        className={`relative ${sizeClasses} rounded-full flex items-center justify-center transition-all duration-200 ${config.color} ${
          state === "processing" ? "opacity-70 cursor-wait" : "active:scale-95 cursor-pointer"
        } border shadow-sm`}
        title={config.label}
        aria-label={config.label}
      >
        {state === "idle" && <Mic className={iconSize} />}
        {state === "wake-listening" && <Radio className={`${iconSize} animate-pulse`} />}
        {state === "listening" && <MicOff className={iconSize} />}
        {state === "processing" && <Loader2 className={`${iconSize} animate-spin`} />}
        {state === "speaking" && <Volume2 className={`${iconSize} animate-pulse`} />}
      </button>
    </div>
  );
}

// ─── Voice Status Bar ─────────────────────────────────────────────────────────

interface VoiceStatusBarProps {
  state: VoiceState;
  volume: number;
}

export function VoiceStatusBar({ state, volume }: VoiceStatusBarProps) {
  if (state === "idle") return null;

  const config = stateConfig[state];

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] animate-fade-in bg-card border-border">
      {state === "listening" && (
        <>
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-red-400 font-medium">Escutando...</span>
          {/* Volume bars */}
          <div className="flex items-end gap-0.5 h-3">
            {[0.15, 0.3, 0.5, 0.7, 0.85].map((threshold, i) => (
              <div
                key={i}
                className={`w-0.5 rounded-full transition-all duration-75 ${
                  volume > threshold ? "bg-red-400" : "bg-muted-foreground/20"
                }`}
                style={{ height: `${4 + i * 2}px` }}
              />
            ))}
          </div>
          <span className="text-muted-foreground ml-auto">Clique ou espere 6s para enviar</span>
        </>
      )}
      {state === "wake-listening" && (
        <>
          <Radio className="w-3 h-3 text-yellow-500 animate-pulse" />
          <span className="text-yellow-500 font-medium">Diga "Olá Megan" para começar</span>
        </>
      )}
      {state === "processing" && (
        <>
          <Loader2 className="w-3 h-3 text-primary animate-spin" />
          <span className="text-primary font-medium">Transcrevendo áudio...</span>
        </>
      )}
      {state === "speaking" && (
        <>
          <Volume2 className="w-3 h-3 text-primary animate-pulse" />
          <span className="text-primary font-medium">Megan está falando...</span>
          <span className="text-muted-foreground ml-auto">Clique para parar</span>
        </>
      )}
    </div>
  );
}
